import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Link, Navigate } from "react-router-dom";
import Logo from "../components/Logo";
import {
  Users,
  Search,
  Shield,
  ShieldCheck,
  ShieldX,
  Store,
  CreditCard,
  Clock,
  ChevronLeft,
  Loader2,
  User,
  Crown,
  Trash2,
  AlertTriangle
} from "lucide-react";

const ADMIN_EMAILS = ["reynir@replydriven.com"];

const AdminUsers = () => {
  const { user } = useAuth();
  const [searchEmail, setSearchEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState(null);
  
  const [lifetimeUsers, setLifetimeUsers] = useState([]);
  const [loadingLifetime, setLoadingLifetime] = useState(true);
  
  const [grantModal, setGrantModal] = useState({ open: false, email: "" });
  const [grantReason, setGrantReason] = useState("");
  const [granting, setGranting] = useState(false);
  
  const [revokeModal, setRevokeModal] = useState({ open: false, email: "", name: "" });
  const [revoking, setRevoking] = useState(false);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const fetchLifetimeUsers = useCallback(async () => {
    try {
      setLoadingLifetime(true);
      const res = await api.get("/admin/users/lifetime");
      setLifetimeUsers(res.data?.users || []);
    } catch (err) {
      console.error("Failed to fetch lifetime users:", err);
    } finally {
      setLoadingLifetime(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchLifetimeUsers();
    }
  }, [isAdmin, fetchLifetimeUsers]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchEmail.trim()) return;
    
    setSearching(true);
    setSearchError(null);
    setSearchResult(null);
    
    try {
      const res = await api.post("/admin/users/search", { email: searchEmail.trim() });
      setSearchResult(res.data);
    } catch (err) {
      setSearchError(err.response?.data?.detail || "User not found");
    } finally {
      setSearching(false);
    }
  };

  const handleGrant = async () => {
    if (!grantReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    
    setGranting(true);
    try {
      await api.post("/admin/users/grant-lifetime", {
        email: grantModal.email,
        reason: grantReason.trim()
      });
      toast.success("Lifetime access granted.");
      setGrantModal({ open: false, email: "" });
      setGrantReason("");
      
      // Refresh data
      if (searchResult?.email === grantModal.email) {
        const res = await api.post("/admin/users/search", { email: grantModal.email });
        setSearchResult(res.data);
      }
      fetchLifetimeUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to grant access");
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await api.post("/admin/users/revoke-lifetime", { email: revokeModal.email });
      toast.success("Lifetime access revoked.");
      setRevokeModal({ open: false, email: "", name: "" });
      
      // Refresh data
      if (searchResult?.email === revokeModal.email) {
        const res = await api.post("/admin/users/search", { email: revokeModal.email });
        setSearchResult(res.data);
      }
      fetchLifetimeUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke access");
    } finally {
      setRevoking(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (!user) return <Navigate to="/" />;
  if (!isAdmin) return <Navigate to="/dashboard" />;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo showText linkTo="/" />
            <span className="text-text-muted">/</span>
            <Link to="/dashboard" className="text-text-muted hover:text-white text-sm">Dashboard</Link>
            <span className="text-text-muted">/</span>
            <span className="text-white text-sm font-medium">Admin</span>
          </div>
          
          <Link to="/dashboard">
            <Button variant="ghost" className="text-text-muted hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
        </div>
      </nav>

      {/* Admin Sub-Navigation */}
      <div className="fixed top-[65px] left-0 right-0 z-40 bg-[#0a0a0a] border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-1">
            <Link
              to="/admin"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Overview
            </Link>
            <Link
              to="/admin/affiliates"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Affiliates
            </Link>
            <Link
              to="/admin/users"
              className="px-4 py-3 text-sm font-medium border-b-2 border-[#D4F34A] text-white"
            >
              Users
            </Link>
            <Link
              to="/admin/customers"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Customers
            </Link>
            <Link
              to="/admin/payouts"
              className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-text-muted hover:text-white"
            >
              Payouts
            </Link>
          </div>
        </div>
      </div>

      <main className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
              <Users className="w-8 h-8 text-[#D4F34A]" />
              Users
            </h1>
            <p className="text-text-muted text-sm mt-1">
              Grant or revoke lifetime access (billing exempt).
            </p>
          </div>

          {/* Section A: Search */}
          <Card className="bg-surface border-[#262626] mb-6">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="w-5 h-5 text-text-muted" />
                Find user by email
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-3">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    className="bg-[#1a1a1a] border-[#262626] text-white"
                    data-testid="search-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={searching || !searchEmail.trim()}
                  className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"
                  data-testid="search-btn"
                >
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
                </Button>
              </form>
              <p className="text-xs text-text-muted mt-2">Search is exact match (email).</p>
            </CardContent>
          </Card>

          {/* Section B: Search Result */}
          {searchError && (
            <Card className="bg-red-500/10 border-red-500/30 mb-6">
              <CardContent className="py-4">
                <p className="text-red-400">{searchError}</p>
              </CardContent>
            </Card>
          )}

          {searchResult && (
            <Card className="bg-surface border-[#262626] mb-6" data-testid="user-result-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-text-muted" />
                  User
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Left column - User info */}
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-text-muted uppercase">Email</p>
                      <p className="text-white font-medium">{searchResult.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted uppercase">User ID</p>
                      <p className="text-white font-mono text-sm">{searchResult.user_id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted uppercase">Billing</p>
                      <p className={`font-medium ${
                        searchResult.billing_label === "Lifetime access" ? "text-[#D4F34A]" :
                        searchResult.billing_label === "Active subscription" ? "text-green-400" :
                        searchResult.billing_label === "Trial" ? "text-yellow-400" :
                        "text-text-muted"
                      }`}>
                        {searchResult.billing_label}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted uppercase">Plan</p>
                      <p className="text-white">{searchResult.subscription_plan || "none"}</p>
                    </div>
                  </div>
                  
                  {/* Right column - More details */}
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-text-muted uppercase">Subscription</p>
                      <p className="text-white font-mono text-sm">{searchResult.shopify_subscription_id || "None"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted uppercase">Stores connected</p>
                      <p className="text-white">{searchResult.connected_store_count}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted uppercase">Lifetime access</p>
                      <p className={searchResult.lifetime_access ? "text-[#D4F34A] font-medium" : "text-text-muted"}>
                        {searchResult.lifetime_access ? "Enabled" : "Disabled"}
                        {searchResult.is_admin && !searchResult.lifetime_access && " (Admin bypass)"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted uppercase">Lifetime reason</p>
                      <p className="text-white">{searchResult.lifetime_reason || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-muted uppercase">Granted at</p>
                      <p className="text-white">{formatDate(searchResult.lifetime_granted_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-6 pt-4 border-t border-[#262626] flex justify-end gap-3">
                  {!searchResult.lifetime_access ? (
                    <Button
                      onClick={() => setGrantModal({ open: true, email: searchResult.email })}
                      className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"
                      data-testid="grant-lifetime-btn"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Grant lifetime access
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setRevokeModal({ open: true, email: searchResult.email, name: searchResult.name })}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="revoke-lifetime-btn"
                    >
                      <ShieldX className="w-4 h-4 mr-2" />
                      Revoke lifetime access
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Section C: Lifetime Users Table */}
          <Card className="bg-surface border-[#262626]">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#D4F34A]" />
                Lifetime access users
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingLifetime ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-text-muted" />
                </div>
              ) : lifetimeUsers.length === 0 ? (
                <p className="text-text-muted text-center py-8">No lifetime users yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#262626]">
                        <th className="text-left py-3 px-2 text-xs font-medium text-text-muted uppercase">Email</th>
                        <th className="text-left py-3 px-2 text-xs font-medium text-text-muted uppercase">Reason</th>
                        <th className="text-left py-3 px-2 text-xs font-medium text-text-muted uppercase">Granted</th>
                        <th className="text-right py-3 px-2 text-xs font-medium text-text-muted uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#262626]">
                      {lifetimeUsers.map((u) => (
                        <tr key={u.email} className="hover:bg-white/5">
                          <td className="py-3 px-2">
                            <p className="text-white font-medium">{u.email}</p>
                            {u.name && <p className="text-xs text-text-muted">{u.name}</p>}
                          </td>
                          <td className="py-3 px-2 text-sm text-text-muted max-w-xs truncate">
                            {u.lifetime_reason || "—"}
                          </td>
                          <td className="py-3 px-2 text-sm text-text-muted">
                            {formatDate(u.lifetime_granted_at)}
                          </td>
                          <td className="py-3 px-2 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setRevokeModal({ open: true, email: u.email, name: u.name })}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Grant Modal */}
      <Dialog open={grantModal.open} onOpenChange={(open) => !granting && setGrantModal({ open, email: open ? grantModal.email : "" })}>
        <DialogContent className="bg-[#1a1a1a] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-[#D4F34A]" />
              Grant lifetime access
            </DialogTitle>
            <DialogDescription className="text-text-muted">
              This user will bypass billing and keep access even without a subscription.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="reason" className="text-sm text-text-muted">
              Reason (required)
            </Label>
            <Input
              id="reason"
              placeholder='e.g. "Early tester - Iceland Shopify stores"'
              value={grantReason}
              onChange={(e) => setGrantReason(e.target.value)}
              className="mt-2 bg-[#0a0a0a] border-[#262626] text-white"
              data-testid="grant-reason-input"
            />
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setGrantModal({ open: false, email: "" })}
              disabled={granting}
              className="text-text-muted hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGrant}
              disabled={granting || !grantReason.trim()}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90"
              data-testid="confirm-grant-btn"
            >
              {granting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Grant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Modal */}
      <Dialog open={revokeModal.open} onOpenChange={(open) => !revoking && setRevokeModal({ open, email: open ? revokeModal.email : "", name: open ? revokeModal.name : "" })}>
        <DialogContent className="bg-[#1a1a1a] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Revoke lifetime access
            </DialogTitle>
            <DialogDescription className="text-text-muted">
              This user will require an active subscription to access paid features.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-white">
              Are you sure you want to revoke lifetime access for <span className="font-medium">{revokeModal.email}</span>?
            </p>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRevokeModal({ open: false, email: "", name: "" })}
              disabled={revoking}
              className="text-text-muted hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRevoke}
              disabled={revoking}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-revoke-btn"
            >
              {revoking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
